import React, { useMemo, useState, useEffect } from 'react';
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown';
import Loading from '../common/Loading';
import { currencyName, getPrice, handleResponse } from '../common/Helpers';
import { XRPSCAN_API_URL } from '../../config';
import AccountTag from '../lib/AccountTag';
import EmptyStateNoRecord from '../lib/EmptyStateNoRecord';
import AdvancedTable from '../lib/AdvancedTable';
import XBadge from '../lib/XBadge';
import XIcon from '../lib/XIcon';
import AccountIcon from '../lib/AccountIcon';
import { FormattedRelative } from 'react-intl';


// Oracle activity cutoff time in JS (milliseconds)
const ORACLE_ACTIVE_CUTOFF = 3600 * 24 * 1000;

const isOracleActive = (LastUpdateTime) => {
    return (new Date().getTime() - ORACLE_ACTIVE_CUTOFF) > LastUpdateTime * 1000
}

const isPriceDataCurrent = (PriceData) => {
    return !!(PriceData?.AssetPrice && PriceData?.Scale)
}

const getPriceDataItem = (data) => {
    return (
        <XBadge variant={isPriceDataCurrent(data?.PriceData) ? "primary" : "danger"} className="text-monospace">
            <span className='mr-1'>
                {currencyName(data?.PriceData?.BaseAsset)}/{currencyName(data?.PriceData?.QuoteAsset)}:
            </span>
            {isPriceDataCurrent(data?.PriceData) ? getPrice(data?.PriceData?.AssetPrice, data?.PriceData?.Scale) : "OUTDATED" }
        </XBadge>
    );
}

const getPriceDataDropdown = (PriceDataSeries) => {
    return (
        <DropdownButton
            id="dropdown-basic-button"
            title={getPriceDataItem(PriceDataSeries[0])}
            size="sm"
            className='text-nowrap'
            bsPrefix="btn btn-sm btn-votes"
        >
            {PriceDataSeries.map((data, index) => (
                <Dropdown.Item key={index}>
                    {getPriceDataItem(data)}
                </Dropdown.Item>
            ))}
        </DropdownButton>
    )
}

/*
* Add custom properties to represent Money etc.
*/
const addProperties = (_oracles) => {
	const populatedOracles = _oracles.map((oracle, idx) => {
        oracle.idx = idx + 1

        if (oracle?.OracleName?.twitter) {
            oracle._Icon = <AccountIcon twitter={oracle.OracleName?.twitter} />
        }

        if (oracle?.Owner) {
            oracle._Oracle = <>
                <AccountTag name={oracle.OracleName} link>
                    {oracle.Owner}
                </AccountTag>
            </>
        }

        if (oracle?.Provider) {
            oracle._Provider = <XBadge className="text-monospace">{oracle.Provider}</XBadge>
        }

        if (oracle?.AssetClass) {
            oracle._AssetClass = <XBadge variant="success" className="text-monospace">{oracle.AssetClass}</XBadge>
        }

        if (Array.isArray(oracle?.PriceDataSeries) && oracle?.PriceDataSeries.length > 0) {
            oracle._PriceData = getPriceDataDropdown(oracle.PriceDataSeries)
        }

        if (oracle?.LastUpdateTime) {
            oracle._LastUpdateTime = <span className={`text-nowrap ${isOracleActive(oracle.LastUpdateTime) && "table-warning"}`}>
                {isOracleActive(oracle.LastUpdateTime) &&
                <XIcon icon="exclamation-circle" className="mr-2 text-danger" faStyle="far" />
                }
                <FormattedRelative value={oracle.LastUpdateTime * 1000} updateInterval={3000} />
            </span>
        }

        if (oracle?.URI) {
            oracle._Attestation = <a href={oracle.URI} target="_blank" rel="noopener noreferrer nofollow">
                <XIcon icon="file-certificate"/>
            </a>
        }

        return oracle;
	});
	return populatedOracles;
}

const OracleTable = () => {
	const [ oracles, setOracles ] = useState([])
	const [ loading, setLoading ] = useState(false);

	const columns = useMemo(
		() => [
			{
				Header: "Price data submitted by oracle services",
				columns: [
                    {
                        Header: "#",
                        accessor: "idx",
                    },
                    {
                        accessor: "_Icon",
                        className: "text-right"
                    },
                    {
                        Header: "Oracle",
                        accessor: "_Oracle",
                        className: "text-left",
                    },
                    {
                        Header: "Provider",
                        accessor: "_Provider",
                        className: "text-right text-nowrap",
                    },
                    {
                        Header: "Asset class",
                        accessor: "_AssetClass",
                        className: "text-right text-nowrap",
                    },
                    {
                        Header: "Price Data",
                        accessor: "_PriceData",
                        className: "text-right",
                    },
                    {
                        Header: "Last submitted",
                        accessor: "_LastUpdateTime",
                        className: "text-right",
                    },
                    {
                        Header: "Attestation",
                        accessor: "_Attestation",
                        className: "text-center",
                    },
				],
			},
			],
      [],
	)

    /*
    * Fetch all Oracles
    */
    useEffect(() => {
        setLoading(true);
        fetch(`${XRPSCAN_API_URL}/oracles`)
        .then(handleResponse)
        .then((data) => {
            setOracles(addProperties(data));
        })
        .catch(() => {
            setOracles([]);
        })
        .finally(() => {
            setLoading(false);
        });
        return () => {
            setOracles([]);
        }
    }, []);

	if (loading) {
		return <Loading/>;
	}

	if (!loading && oracles && oracles.length === 0) {
		return (
			<EmptyStateNoRecord title="Oracles">
				Unable to fetch Price Oracles.
			</EmptyStateNoRecord>
		)
	} else {
		return <AdvancedTable
			columns={columns}
			data={oracles}
			// filterProperty='Owner'
			// filterPlaceholder='Search Oracle accounts'
    		defaultPageSizes={[50, 100, 1000]}
    		initialPageSize={50}
		/>
	}
}

export default OracleTable;